import {
  Box,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField,
  CircularProgress,
  Popper,
  ListItem
} from '@mui/material';
import DropdownPopover from 'src/components/UI/DropdownPopover';
import { Accordion } from 'src/components/UI/Accordion';
import { Input } from 'src/components/UI/Input';
import { TextArea } from 'src/components/UI/TextArea';
import { Controller, useController, useFormContext } from 'react-hook-form';
import { errorMessageSx } from 'src/components/UI/Input.styles';
import { ConfirmAndPayFormData } from './index';
import { useEffect, useRef, useState } from 'react';
import { getBusinessCategory } from 'src/common/Scraper';
import {
  iso2ToIso3,
  normalize
} from 'src/content/localSeo/AddLocationDetailsComponents/funtions';
import { MapRankCss } from 'src/content/mapRankTracker/MapRankCss';

// Generate time options in 30-minute intervals
const generateTimeOptions = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute
        .toString()
        .padStart(2, '0')}`;
      times.push(timeString);
    }
  }
  return times;
};

const timeOptions = generateTimeOptions();
const statusOptions = ['Open', 'Closed'];

// Mapping between display labels and form values
const statusDisplayToValue: Record<string, string> = {
  Open: 'open',
  Closed: 'closed'
};

const statusValueToDisplay: Record<string, string> = {
  open: 'Open',
  closed: 'Closed'
};

const daysOfWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

const paymentMethods = [
  'Visa',
  'Mastercard',
  'Cash',
  'Personal Check',
  "Traveler's Checks",
  'American Express',
  'Invoice',
  'Insurance',
  'ATM/Debit',
  'PayPal',
  'Financing Available',
  'Discover'
];

const socialMediaPlatforms = [
  {
    name: 'Facebook',
    iconPath: '/static/images/icons/local-seo/Facebook.svg',
    baseUrl: 'https://www.facebook.com/'
  },
  {
    name: 'Linkedin',
    iconPath: '/static/images/icons/local-seo/linkedin.svg',
    baseUrl: 'https://www.linkedin.com/'
  },
  {
    name: 'X',
    iconPath: '/static/images/icons/local-seo/X.svg',
    baseUrl: 'https://www.x.com/'
  },
  {
    name: 'Instagram',
    iconPath: '/static/images/icons/local-seo/instagram.svg',
    baseUrl: 'https://www.instagram.com/'
  },
  {
    name: 'Pinterest',
    iconPath: '/static/images/icons/local-seo/pinterest.svg',
    baseUrl: 'https://www.pinterest.com/'
  },
  {
    name: 'Youtube',
    iconPath: '/static/images/icons/local-seo/youtube.svg',
    baseUrl: 'https://www.youtube.com/'
  },
  {
    name: 'TikTok',
    iconPath: '/static/images/icons/local-seo/tiktok.svg',
    baseUrl: 'https://www.tiktok.com/'
  }
];

export const AboutTheBusiness = ({
  locationDetails
}: {
  locationDetails?: any;
}) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue
  } = useFormContext<ConfirmAndPayFormData>();
  const categoryField = watch('aboutTheBusiness.businessCategory');
  const { field } = useController({
    name: 'aboutTheBusiness.businessCategory',
    defaultValue: null
  });
  const openingHours = watch('aboutTheBusiness.openingHours');
  const selectedPaymentMethods = watch('aboutTheBusiness.paymentMethods') || [];
  const countryWatched = watch('businessLocationInfo.country');
  const countrySelected =
    countryWatched || locationDetails?.storefrontAddress?.regionCode;
  const [businessCategories, setBusinessCategories] = useState<any[] | null>(
    null
  );
  const [categoryLoader, setCategoryLoader] = useState(false);
  const [autoSelectedCategoryId, setAutoSelectedCategoryId] = useState<
    string | null
  >(null);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [didSyncAutoFill, setDidSyncAutoFill] = useState(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

  const isAutoFillingRef = useRef(false);

  const deduplicateById = (arr: any[]) =>
    Array.from(new Map(arr.map((item) => [item.id, item])).values());

  const getBusinessCategoryDetails = async (
    code: string,
    categoryName: string | null = null
  ) => {
    setCategoryLoader(true);

    if (!code) {
      setBusinessCategories([]);
      setCategoryLoader(false);
      return;
    }

    try {
      const payload: any = { country: code };
      if (categoryName) payload.categoryName = categoryName;

      const response: any = await getBusinessCategory(payload);

      const items = response?.category?.items || [];
      const selectedId = response?.selectedCategoryId;

      // Store FIRST so options are present immediately
      setBusinessCategories(items);

      // If categoryName is null → this is a manual search full-list fetch

      // Auto-fill logic
      if (categoryName && selectedId && !autoSelectedCategoryId) {
        setAutoSelectedCategoryId(selectedId);

        const foundCategory = items.find((c: any) => c.id === selectedId);

        if (foundCategory) {
          // ensure options include it first (so Autocomplete can match)
          setBusinessCategories((prev) => {
            return deduplicateById([foundCategory, ...prev]);
          });

          // set RHF value
          setValue('aboutTheBusiness.businessCategory', {
            id: foundCategory.id,
            name: foundCategory.name
          });
        } else {
          const synthetic = { id: selectedId, name: categoryName };

          // Inject synthetic option so it's present in options[]
          setBusinessCategories((prev) =>
            deduplicateById([synthetic, ...prev])
          );

          // Immediately set the form value so the controller has the actual object
          setValue('aboutTheBusiness.businessCategory', synthetic);

          // FIX: ensure popper stays closed after auto-fill
          setInputValue('');
          setOpen(false);
        }
      }

      setCategoryLoader(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setBusinessCategories([]);
      setCategoryLoader(false);
    }
  };

  const handlePaymentMethodChange = (method: string) => {
    const currentMethods = selectedPaymentMethods || [];
    const newMethods = currentMethods.includes(method)
      ? currentMethods.filter((m) => m !== method)
      : [...currentMethods, method];
    setValue('aboutTheBusiness.paymentMethods', newMethods);
  };

  // Initial category fetch logic based on location info
  useEffect(() => {
    if (locationDetails) {
      const countryCode = locationDetails?.storefrontAddress?.regionCode;
      const categoryName = locationDetails?.categories?.displayName || null;
      const iso3 = countryCode ? iso2ToIso3[countryCode] : null;

      if (iso3) {
        getBusinessCategoryDetails(iso3, categoryName);
      }
    }
  }, [locationDetails]);

  useEffect(() => {
    // 1. If user manually cleared the input → fully reset state and exit
    const userClearedInput =
      inputValue === '' &&
      field.value?.name && // form still has old value
      didSyncAutoFill && // auto-fill already happened previously
      !isAutoFillingRef.current; // not currently running auto-fill

    if (userClearedInput) {
      setValue('aboutTheBusiness.businessCategory', null);
      setAutoSelectedCategoryId(null);
      setDidSyncAutoFill(false);
      return;
    }

    // 2. Run auto-fill only once when API sets the selected value
    if (!didSyncAutoFill && autoSelectedCategoryId && field.value?.name) {
      isAutoFillingRef.current = true;
      setInputValue(field.value.name);
      setDidSyncAutoFill(true);

      // small delay to avoid feedback loop
      setTimeout(() => {
        isAutoFillingRef.current = false;
      }, 0);

      return;
    }
  }, [field.value, autoSelectedCategoryId, inputValue, didSyncAutoFill]);

  const handleInputChange = (event, newInput, reason) => {
    // Ignore MUI internal resets
    if (reason === 'reset') {
      return;
    }

    // User cleared input manually → clear RHF field
    if (reason === 'input' && newInput === '') {
      setValue('aboutTheBusiness.businessCategory', null);
      setAutoSelectedCategoryId(null);
      setDidSyncAutoFill(false);
    }

    setInputValue(newInput);
  };

  const handleCategoryFocus = () => {
    if (!hasFetchedOnce) {
      const countryCode = locationDetails?.storefrontAddress?.regionCode;
      const iso3 = countryCode ? iso2ToIso3[countryCode] : null;

      if (iso3) {
        setHasFetchedOnce(true);
        getBusinessCategoryDetails(iso3, null);
      }
    }
  };

  return (
    <Accordion title="About The Business" defaultExpanded={true}>
      <Typography
        sx={{
          fontFamily: 'Inter',
          fontWeight: 400,
          fontSize: { xs: '13px', sm: '14px' },
          lineHeight: { xs: '18px', sm: '20px' },
          color: 'var(--color-text-purple)',
          marginBottom: { xs: '20px', sm: '24px' }
        }}
      >
        We&apos;ll include your business description, services, and photos in
        each submission. Please keep in mind that some directories may not
        display this content, or may only show it on paid listings, which
        aren&apos;t included in SEOSpace Citation Builder.
      </Typography>

      <Box sx={{ marginBottom: { xs: '24px', sm: '32px' } }}>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="aboutTheBusiness.businessCategory"
              control={control}

              render={({ field, fieldState }) => {
                const filteredCategories = countrySelected
                  ? deduplicateById(businessCategories || [])
                  : [];

                return (
                  <>
                    <Typography
                      sx={{
                        fontSize: 'var(--font-size-14)',
                        fontWeight: 500,
                        color: 'var(--color-text-secondary)',
                        marginBottom: '6px',
                        lineHeight: 'var(--line-height-20)'
                      }}
                    >
                      Business Category *
                    </Typography>

                    <Autocomplete
                      inputValue={inputValue}
                      options={filteredCategories}
                      open={open}
                      onOpen={() => {
                        if (inputValue.length >= 3) setOpen(true);
                      }}
                      onClose={() => setOpen(false)}
                      onFocus={handleCategoryFocus}
                      onInputChange={handleInputChange}
                      getOptionLabel={(option) => option?.name || ''}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      value={field.value || null}
                      onChange={(_e, newValue) => {
                        if (!newValue) {
                          setDidSyncAutoFill(true);
                          setInputValue('');
                          field.onChange(null);
                          return;
                        }

                        // REQUIRED: reflect selected value in input
                        setInputValue(newValue.name); // <-- Missing piece

                        field.onChange(newValue);
                      }}
                      loading={
                        (!businessCategories && !!countrySelected) ||
                        categoryLoader
                      }
                      disableClearable
                      popupIcon={null}
                      disabled={!countrySelected || categoryLoader}
                      filterOptions={(options, { inputValue }) => {
                        const query = normalize(inputValue || '');
                        if (query.length < 3) return [];

                        const queryWords = query.split(' ').filter(Boolean);

                        const filtered = (options || []).filter((option) => {
                          const name = normalize(option?.name || '');
                          return (
                            name.includes(query) ||
                            queryWords.every((word) =>
                              new RegExp(`\\b${word}\\b`, 'i').test(name)
                            )
                          );
                        });

                        return filtered.sort((a, b) => {
                          const aName = normalize(a?.name || '');
                          const bName = normalize(b?.name || '');
                          const aIndex = aName.indexOf(query);
                          const bIndex = bName.indexOf(query);
                          return aIndex === bIndex
                            ? aName.localeCompare(bName)
                            : aIndex - bIndex;
                        });
                      }}
                      noOptionsText={
                        !countrySelected
                          ? 'Select a country first'
                          : inputValue.length < 3
                          ? 'Type at least 3 characters'
                          : 'No results found'
                      }
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          paddingRight: '8px',
                          height: '47px',
                          borderRadius: '8px'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: fieldState.error ? 'var(--color-error-red)' : 'var(--color-border)'
                        },
                        '& .MuiAutocomplete-input': {
                          padding: '0px'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: fieldState.error ? 'var(--color-error-red)' : 'var(--color-text-placeholder)'
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Enter Business Category"
                          variant="outlined"
                          error={!!fieldState.error}
                          helperText={
                            fieldState.error?.message ||
                            (fieldState.error as any)?.name?.message
                          }
                          InputProps={{
                            ...params.InputProps,
                            readOnly: !countrySelected,
                            endAdornment: (
                              <>
                                {(!businessCategories && !!countrySelected) ||
                                categoryLoader ? (
                                  <CircularProgress size={16} />
                                ) : null}
                              </>
                            )
                          }}
                          sx={{
                            '& input::placeholder': {
                              fontSize: 'var(--font-size-16)',
                              opacity: 1
                            },
                            '& .MuiAutocomplete-input': {
                              padding: '0px 0px 0px 8px!important',
                              color:
                                inputValue?.length > 0 ? 'inherit' : 'var(--color-text-placeholder)'
                            }
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <ListItem
                          {...props}
                          key={option.id}
                          sx={{
                            ...MapRankCss.autoCompleteListItem,
                            color: 'var(--color-text-medium-grey)',
                            fontSize: 'var(--font-size-14)',
                            paddingY: '6px'
                          }}
                        >
                          <Typography sx={MapRankCss.autoCompleteName}>
                            {option?.name}
                          </Typography>
                        </ListItem>
                      )}
                      PopperComponent={(props: any) => {
                        const anchorEl = props.anchorEl as HTMLElement;
                        return (
                          <Popper
                            {...props}
                            placement="bottom-start"
                            style={{
                              width: anchorEl?.clientWidth || undefined,
                              zIndex: 1300
                            }}
                          />
                        );
                      }}
                    />
                  </>
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="aboutTheBusiness.numberOfEmployees"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Number of Employees"
                  fullWidth
                  placeholder="Enter Here"
                  error={!!errors.aboutTheBusiness?.numberOfEmployees}
                  helperText={
                    errors.aboutTheBusiness?.numberOfEmployees?.message
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="aboutTheBusiness.businessDescription"
              control={control}
              render={({ field }) => (
                <TextArea
                  value={field.value || ''}
                  onChange={(value) => field.onChange(value)}
                  maxLength={750}
                  label="Description of the Business"
                  placeholder="Describe your business here"
                  error={!!errors.aboutTheBusiness?.businessDescription}
                  helperText={
                    errors.aboutTheBusiness?.businessDescription?.message as
                      | string
                      | undefined
                  }
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Opening Hours Section */}
      <Box sx={{ marginBottom: { xs: '24px', sm: '32px' } }}>
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: 'var(--font-size-16)',
            lineHeight: 'var(--line-height-24)',
            color: 'var(--color-primary)',
            marginBottom: { xs: '16px', sm: '20px' }
          }}
        >
          Opening Hours
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {daysOfWeek.map((day) => (
            <Box
              key={day}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch'
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  fontSize: 'var(--font-size-14)',
                  lineHeight: 'var(--line-height-20)',
                  color: 'var(--color-text-secondary)',
                  minWidth: { xs: 'auto', sm: '100px' },
                  width: { xs: '100%', sm: 'auto' },
                  marginBottom: '6px'
                }}
              >
                {day}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  flex: 1,
                  flexWrap: 'wrap',
                  marginBottom:
                    day !== daysOfWeek[daysOfWeek.length - 1] ? '16px' : 0
                }}
              >
                <Box
                  sx={{
                    minWidth: { xs: '120px', sm: '140px' },
                    flex: 1
                  }}
                >
                  <Controller
                    name={`aboutTheBusiness.openingHours.${day}.status`}
                    control={control}
                    render={({ field }) => {
                      // Convert form value (lowercase) to display value (capitalized)
                      const displayValue =
                        statusValueToDisplay[field.value || 'closed'] ||
                        'Closed';
                      return (
                        <DropdownPopover
                          selectedValue={displayValue}
                          options={statusOptions}
                          onSelect={(value) => {
                            // Convert display value (capitalized) to form value (lowercase)
                            const formValue =
                              statusDisplayToValue[value as string] ||
                              value.toLowerCase();
                            field.onChange(formValue);
                            if (formValue === 'closed') {
                              setValue(
                                `aboutTheBusiness.openingHours.${day}.hours`,
                                [],
                                { shouldValidate: true }
                              );
                            } else {
                              const dayHours = openingHours?.[day] as
                                | {
                                    hours?: Array<{
                                      start: string;
                                      end: string;
                                    }>;
                                  }
                                | undefined;
                              if (
                                !dayHours?.hours ||
                                dayHours.hours.length === 0
                              ) {
                                setValue(
                                  `aboutTheBusiness.openingHours.${day}.hours`,
                                  [],
                                  { shouldValidate: true }
                                );
                              }
                            }
                          }}
                          placeholder="—"
                          styles={{
                            filterBoxSx: {
                              minWidth: '120px',
                              width: '100%'
                            }
                          }}
                        />
                      );
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    minWidth: { xs: '100px', sm: '110px' },
                    flex: 1
                  }}
                >
                  <Controller
                    name={`aboutTheBusiness.openingHours.${day}.hours.0.start`}
                    control={control}
                    render={({ field }) => {
                      const dayHours = openingHours?.[day]?.hours;
                      const startValue =
                        dayHours && dayHours.length > 0
                          ? dayHours[0].start
                          : '';
                      return (
                        <DropdownPopover
                          selectedValue={startValue}
                          options={timeOptions}
                          onSelect={(value) => {
                            const currentHours =
                              openingHours?.[day]?.hours || [];
                            const updatedHours =
                              currentHours.length > 0
                                ? [
                                    { ...currentHours[0], start: value },
                                    ...currentHours.slice(1)
                                  ]
                                : [
                                    {
                                      start: value,
                                      end: currentHours[0]?.end || ''
                                    }
                                  ];
                            setValue(
                              `aboutTheBusiness.openingHours.${day}.hours`,
                              updatedHours,
                              { shouldValidate: true }
                            );
                          }}
                          placeholder="—"
                          styles={{
                            filterBoxSx: {
                              minWidth: '100px',
                              width: '100%'
                            }
                          }}
                        />
                      );
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: 'var(--font-size-14)',
                    lineHeight: 'var(--line-height-20)',
                    color: 'var(--color-text-dark2)'
                  }}
                >
                  to
                </Typography>
                <Box
                  sx={{
                    minWidth: { xs: '100px', sm: '110px' },
                    flex: 1
                  }}
                >
                  <Controller
                    name={`aboutTheBusiness.openingHours.${day}.hours.0.end`}
                    control={control}
                    render={({ field }) => {
                      const dayHours = openingHours?.[day]?.hours;
                      const endValue =
                        dayHours && dayHours.length > 0 ? dayHours[0].end : '';
                      return (
                        <DropdownPopover
                          selectedValue={endValue}
                          options={timeOptions}
                          onSelect={(value) => {
                            const currentHours =
                              openingHours?.[day]?.hours || [];
                            const updatedHours =
                              currentHours.length > 0
                                ? [
                                    { ...currentHours[0], end: value },
                                    ...currentHours.slice(1)
                                  ]
                                : [
                                    {
                                      start: currentHours[0]?.start || '',
                                      end: value
                                    }
                                  ];
                            setValue(
                              `aboutTheBusiness.openingHours.${day}.hours`,
                              updatedHours,
                              { shouldValidate: true }
                            );
                          }}
                          placeholder="—"
                          styles={{
                            filterBoxSx: {
                              minWidth: '100px',
                              width: '100%'
                            }
                          }}
                        />
                      );
                    }}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Payment Methods Accepted Section */}
      <Box sx={{ marginBottom: { xs: '24px', sm: '32px' } }}>
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: 'var(--font-size-16)',
            lineHeight: 'var(--line-height-24)',
            color: 'var(--color-primary)',
            marginBottom: '12px'
          }}
        >
          Payment Methods Accepted
        </Typography>
        <Controller
          name="aboutTheBusiness.paymentMethods"
          control={control}
          render={({ field }) => (
            <>
              <Grid container>
                {paymentMethods.map((method) => (
                  <Grid item xs={12} sm={6} md={3} key={method}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(field.value || []).includes(method)}
                          onChange={() => handlePaymentMethodChange(method)}
                          sx={{
                            '& .MuiSvgIcon-root': {
                              fontSize: 'var(--font-size-20)'
                            },
                            color: 'var(--color-text-mid)',
                            '&.Mui-checked': {
                              color: 'var(--color-primary)'
                            }
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 400,
                            fontSize: 'var(--font-size-14)',
                            lineHeight: 'var(--line-height-20)',
                            color: 'var(--color-text-dark2)'
                          }}
                        >
                          {method}
                        </Typography>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
              {errors.aboutTheBusiness?.paymentMethods && (
                <Typography sx={errorMessageSx}>
                  {errors.aboutTheBusiness.paymentMethods.message as string}
                </Typography>
              )}
            </>
          )}
        />
      </Box>

      {/* Social Profile Links Section */}
      <Box>
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: 'var(--font-size-16)',
            lineHeight: 'var(--line-height-24)',
            color: 'var(--color-primary)',
            marginBottom: '12px'
          }}
        >
          Social Profile Links
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, sm: 2.5 }
          }}
        >
          {socialMediaPlatforms.map((platform) => {
            return (
              <Controller
                key={platform.name}
                name={`aboutTheBusiness.socialMediaLinks.${platform.name}`}
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      label={platform.name}
                      fullWidth
                      placeholder={platform.baseUrl}
                      startIcon={
                        <Box
                          component="img"
                          src={process.env.PUBLIC_URL + platform.iconPath}
                          alt={platform.name}
                          sx={{
                            width: '20px',
                            height: '20px',
                            objectFit: 'contain'
                          }}
                        />
                      }
                      error={
                        !!errors.aboutTheBusiness?.socialMediaLinks?.[
                          platform.name
                        ]
                      }
                      helperText={
                        errors.aboutTheBusiness?.socialMediaLinks?.[
                          platform.name
                        ]?.message
                      }
                    />
                  </>
                )}
              />
            );
          })}
        </Box>
      </Box>
    </Accordion>
  );
};
